import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import useDepollution from "../../data/useDepollution";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ICreateUpdateDepollutionStepRequest, IDepollutionStep } from "../../models/Depollution";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SwitchInput from "../shared/Inputs/SwitchInput";
import { TextInput } from "../shared/Inputs/TextInput";

type FormValues = {
  name: string,
  subtitle: string,
  order: number,
  needsValue: boolean,
  options: string,
  isActive: boolean,
};

interface DepollutionStepModalProps {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  depollutionStep?: IDepollutionStep;
  defaultOrder: number;
}

export default function DepollutionStepModal({ open, setOpen, depollutionStep, defaultOrder }: DepollutionStepModalProps) {
  const depollutionLabel = "Depollution";
  const { saveHandlers } = useDefaultCRUDHandlers(`${depollutionLabel} Step`);
  const { deleteHandlers } = useDefaultCRUDHandlers(`${depollutionLabel} Step`);
  const { createDepollutionStep, updateDepollutionStep, deleteDepollutionStep } = useDepollution();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>();

  const onSubmit = (data: FormValues) => {
    const updatedDepollutionStep: ICreateUpdateDepollutionStepRequest = {
      ...depollutionStep,
      name: data.name,
      subtitle: data.subtitle?.trim() || null,
      order: Number(data.order),
      needsValue: data.needsValue,
      options: data.options?.trim() || null,
      isActive: data.isActive,
    };

    if (depollutionStep !== undefined) {
      updateDepollutionStep.mutate(updatedDepollutionStep, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      createDepollutionStep.mutate(updatedDepollutionStep, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  };

  const handleDelete = () => {
    deleteDepollutionStep.mutate(depollutionStep?.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  };

  useEffect(() => {
    if (depollutionStep !== undefined) {
      reset({
        name: depollutionStep.name,
        subtitle: depollutionStep.subtitle ?? "",
        order: depollutionStep.order,
        needsValue: depollutionStep.needsValue,
        options: depollutionStep.options ?? "",
        isActive: depollutionStep.isActive,
      });
    } else {
      reset({
        name: "",
        subtitle: "",
        order: defaultOrder,
        needsValue: false,
        options: "",
        isActive: true,
      });
    }
  }, [depollutionStep, open, reset, defaultOrder]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={`Delete ${depollutionLabel} Step`} message={`Are you sure you want to delete this ${depollutionLabel.toLowerCase()} step? This action cannot be undone.`}
        confirmButtonText="Delete" onConfirm={handleDelete} isLoading={deleteDepollutionStep.isLoading}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen} >
        <h1 className="text-xl">{`${depollutionStep ? "Edit" : "Add"} a ${depollutionLabel} Step`}</h1>
        <h1 className="text-sm text-gray-500">{`All ${depollutionLabel.toLowerCase()} steps will need to be ticked off but can optionally return a value.`}</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)} >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <TextInput label="Name" registerName="name" register={register} required={true} />
              <TextInput label="Subtitle" registerName="subtitle" register={register} />
              <input type="hidden" {...register("order")} />

              <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                <SwitchInput label="Needs Value" registerName="needsValue" register={register} setValue={setValue} watch={watch} />
                <SwitchInput label="Is Active" registerName="isActive" register={register} setValue={setValue} watch={watch} />
              </div>

              <TextInput
                label="Options"
                registerName="options"
                register={register}
                placeholder="Alloy,Steel,Other"
              />
              <p className="text-sm text-gray-500">
                Leave options blank for free text, or enter comma-separated values for a dropdown.
              </p>

              <ButtonInput
                label={`${depollutionStep ? "Save Changes" : "Create"}`}
                isSubmit={true}
                onClick={undefined}
                classes={undefined}
                isLoading={createDepollutionStep.isLoading || updateDepollutionStep.isLoading}
              />
              {depollutionStep !== undefined && (
                <ButtonInput label="Delete" isSubmit={false} onClick={() => (setDeleteOpen(true))} classes="!bg-red-500 hover:!bg-red-600" />
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
